import React, {
  useRef,
  useState,
  useEffect,
  useImperativeHandle,
  useCallback,
} from "react";
import { Icon } from "./universe";
import { Stage, Layer, Image, Line } from "react-konva";
import { Button, Input, Tooltip } from "tdesign-react";
import { Html } from "react-konva-utils";
import { cropToBase64 } from "../../utils/edu.js";
import useImage from "use-image";
import useSmoothScroll from "react-smooth-scroll-hook";
import {
  getScaleRatio,
  getImgNatural,
  getSum,
  stripExifFromUrl,
} from "../../utils/index.js";
import { useImageStore, useUIStore, useMenuStore } from "../../store";
import {
  REGEX,
  ZOOM_STEP,
  ZOOM_MIN,
  ZOOM_MAX,
  PDF_GAP,
  SCROLL_DEBOUNCE_DELAY,
} from "../../const/constants.js";

// 初始尺寸配置
const INITIAL_SIZES = {
  originHeight: 0,
  originWidth: 0,
  width: 100,
  height: 100,
  deg: 0,
};

// 初始旋转信息
const INITIAL_ROTATE_INFO = {
  rotation: 0,
  offsetX: 0,
  offsetY: 0,
};

// ============ 工具函数 ============

/**
 * 判断图片是否为横向
 * @param {number} angle - 旋转角度
 * @returns {boolean|string}
 */
const isImgHorizontal = (angle) => {
  const normalizedDeg = angle % 360;
  const mod = Math.abs((normalizedDeg / 90) % 4);
  return mod === 1 || mod === 3 ? "horizontal" : false;
};

/**
 * 根据角度获取旋转信息 - 参考原始代码的 getRectRotationInfo
 * @param {number} curDeg - 当前旋转角度
 * @param {Object} stageInfo - 舞台信息 { width, height }
 * @returns {Object} 旋转信息
 */
const getRectRotationInfo = (curDeg, stageInfo) => {
  const mod = (curDeg / 90) % 4;

  if (mod === 1 || mod === -3) {
    // 右
    return { rotation: 90, offsetX: 0, offsetY: stageInfo.width };
  }
  if (mod === 3 || mod === -1) {
    // 左
    return { rotation: -90, offsetX: stageInfo.height, offsetY: 0 };
  }
  if (mod === 2 || mod === -2) {
    // 下
    return {
      rotation: -180,
      offsetX: stageInfo.width,
      offsetY: stageInfo.height,
    };
  }
  // 上 (mod === 0)
  return { rotation: 0, offsetX: 0, offsetY: 0 };
};

/**
 * 根据旋转角度计算 HTML 元素位置
 * @param {number} top - 原始 top
 * @param {number} left - 原始 left
 * @param {number} curDeg - 当前旋转角度
 * @returns {Object} 位置对象
 */
const getHtmlPositionByDeg = (top, left, curDeg) => {
  const mod = (curDeg / 90) % 4;

  if (mod === 1 || mod === -3) {
    // 右
    return { top: `${left}px`, left: `${-top}px` };
  }
  if (mod === 3 || mod === -1) {
    // 左
    return { top: `${-left}px`, left: `${top}px` };
  }
  if (mod === 2 || mod === -2) {
    // 下
    return { top: `${-top}px`, left: `${-left}px` };
  }
  // 上 (mod === 0)
  return { top: `${top}px`, left: `${left}px` };
};

/**
 * 防抖函数
 * @param {Function} func - 要防抖的函数
 * @param {number} delay - 延迟时间
 * @returns {Function}
 */
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

// ============ 工具函数：剥离 EXIF ============

/**
 * 剥离 JPEG 图片的 EXIF 数据，返回新的 ObjectURL
// stripExifFromUrl is imported from ../../utils/index.js

// ============ 子组件 ============

/**
 * 图层图片组件（加载前剥离 EXIF，防止浏览器自动旋转图片）
 */
const LayerImage = ({
  src,
  width,
  height,
  x,
  y,
  rotation,
  offsetX,
  offsetY,
  stripExif = false,
}) => {
  const [strippedSrc, setStrippedSrc] = useState(null);
  const [image] = useImage(strippedSrc || "");

  useEffect(() => {
    if (!src) return;
    let objectUrl = null;

    // Only strip EXIF when explicitly enabled (stripExif=true) and src is a real URL (not base64).
    if (!stripExif || src.startsWith("data:")) {
      setStrippedSrc(src);
      return;
    }

    stripExifFromUrl(src).then((url) => {
      objectUrl = url !== src ? url : null; // only track blob URLs for cleanup
      setStrippedSrc(url);
    });

    return () => {
      // 清理上一次生成的 ObjectURL，避免内存泄漏
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [src, stripExif]);

  return (
    <Image
      rotation={rotation}
      offsetX={offsetX}
      offsetY={offsetY}
      image={image}
      width={width}
      height={height}
      y={y}
      x={x}
    />
  );
};

/**
 * 加载中组件
 */
const LoadingOverlay = ({ msg = "文件上传中..." }) => (
  <div className="ocrDemo-resulting-container upload">
    <div className="ocrDemo-icon-resulting"></div>
    <div className="ocrDemo-resulting__text">{msg}</div>
  </div>
);

/**
 * 错误图标 SVG
 */
const ErrorIcon = () => (
  <svg
    width="120"
    height="120"
    viewBox="0 0 120 120"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect opacity="0.01" x="24" y="24" width="72" height="72" fill="white" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M30 84V36H90V84H30Z"
      fill="white"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M39 55.5H81V58.5H39V55.5Z"
      fill="#C1CCDD"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M39 66H69V69H39V66Z"
      fill="#C1CCDD"
    />
    <rect x="30" y="36" width="60" height="10.5" fill="#C1CCDD" />
    <rect opacity="0.01" x="72" y="66" width="30" height="30" fill="white" />
    <circle cx="87" cy="81" r="10.5" fill="white" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M87 69C80.373 69 75 74.373 75 81C75 87.627 80.373 93 87 93C93.627 93 99 87.627 99 81C99 74.373 93.627 69 87 69ZM88.5 87V84H85.5V87H88.5ZM88.5 75V82.5H85.5V75H88.5Z"
      fill="#006EFF"
    />
  </svg>
);
const ThumbnailPreview = ({
  imageList = [],
  imageUrl,
  style,
  showZoomInIcon = true,
  showZoomOutIcon = true,
  showLeftIcon = true,
  showRightIcon = true,
  showZoomIcon = true,
  errorMsg = "图片无法显示",
  onClip = () => {},
  onUpload = () => {},
  onSelect = () => {},
  cRef,
}) => {
  // 从 store 获取状态
  const rects = useImageStore((state) => state.rects);
  const selectRect = useImageStore((state) => state.selectRectKey);
  const angle = useImageStore((state) => state.angle);
  const currentImage = useImageStore((state) => state.currentImage);
  const isImageError = useImageStore((state) => state.isImageError);
  const isLoading = useUIStore((state) => state.isLoading);
  const currentMenu = useMenuStore((state) => state.currentMenu);

  // 派生状态
  const needClip = currentMenu?.actionType === "eduAgent";
  const showLabel = currentMenu?.actionType === "markEdu";
  const isShow = currentImage && !isImageError && !isLoading;

  // State 定义
  const [loading, setLoading] = useState(true);
  const [curImgSize, setCurImgSize] = useState(null);
  const [targetHeight, setTargetHeight] = useState(0);
  const [targetWidth, setTargetWidth] = useState(0);
  const [isError, setIsError] = useState(false);
  const [showPdfPageControl, setShowPdfPageControl] = useState(false);
  const [imgList, setImgList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [inputVal, setInputVal] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [curDeg, setCurDeg] = useState(0);
  const [ratio, setRatio] = useState(0);
  const [sizes, setSizes] = useState(INITIAL_SIZES);
  const [stageInfo, setStageInfo] = useState({ width: 0, height: 0 });
  const [containerImgInfo, setContainerImgInfo] = useState({
    width: 0,
    height: 0,
  });
  const [imgInfo, setImgInfo] = useState({ width: 0, height: 0 });
  const [imgRotateInfo, setImgRotateInfo] = useState(INITIAL_ROTATE_INFO);
  const [rotateInfo, setRotateInfo] = useState(INITIAL_ROTATE_INFO);

  // Refs
  const containerImgInfoRef = useRef(containerImgInfo);
  const imgListRef = useRef(imgList);
  const sizesRef = useRef(sizes);
  const imageBoxRef = useRef(document.body);
  const layerRef = useRef(null);

  const { scrollTo } = useSmoothScroll({
    ref: imageBoxRef,
    speed: 100,
    direction: "y",
  });

  // ============ 工具函数 ============

  /**
   * 获取图片旋转信息
   */
  const getImgRotationInfo = useCallback(
    ({ ratio: r, imgInfo: info, angle: a }) => {
      const normalizedAngle = a % 360;
      if (normalizedAngle === 90) {
        setImgRotateInfo({
          rotation: 90,
          offsetX: 0,
          offsetY: info.height * r,
        });
      } else if (normalizedAngle === 270) {
        setImgRotateInfo({
          rotation: -90,
          offsetX: info.width * r,
          offsetY: 0,
        });
      } else if (normalizedAngle === 180) {
        setImgRotateInfo({
          rotation: -180,
          offsetX: info.width * r,
          offsetY: info.height * r,
        });
      } else {
        setImgRotateInfo(INITIAL_ROTATE_INFO);
      }
    },
    [],
  );

  /**
   * 获取舞台信息（不使用 useCallback，避免闭包问题）
   * 参考原始代码的 handleGetStageInfo
   */
  const handleGetStageInfo = ({
    curDeg: deg,
    angle: a,
    targetHeight: th,
    ratio: r,
    imgInfo: info,
  }) => {
    const horizontal = isImgHorizontal(deg + a);
    const newStageInfo = horizontal
      ? { width: th, height: info.width * r }
      : { width: info.width * r, height: th };

    setStageInfo(newStageInfo);

    // 使用新的舞台信息计算旋转信息
    const rotationInfo = getRectRotationInfo(deg, newStageInfo);
    setRotateInfo(rotationInfo);
  };

  /**
   * 滚动到目标页 - 完全参考原始代码的 scrollToTarget
   * 直接使用 imgList state，每次渲染重新创建函数以捕获最新值
   */
  function scrollToTarget(target) {
    if (imgList?.length <= 0 || target < 0) return;
    const currentScroll = imageBoxRef.current.scrollTop;
    const targetHeight = imgList[target - 1]?.y || 0;
    scrollTo(targetHeight - currentScroll);
  }

  /**
   * 滚动到指定页
   */
  function scrollToPage(page) {
    if (imgList?.length <= 0) return;
    scrollToTarget(page);
    setInputVal(page);
  }

  /**
   * 设置舞台宽度 - 参考原始代码的 handleSetStageWidth
   */
  const handleSetStageWidth = async ({
    imageUrl: imgUrl,
    imageList: imgListParam,
    imgSize,
    sizes: newSizes,
    imageBoxRef: boxRef,
    curDeg: deg,
    stripExif: shouldStripExif = false,
  }) => {
    const curImg = imgListParam?.length > 0 ? imgListParam[0].ImageUrl : imgUrl;
    if (!curImg) {
      setIsError(true);
      return;
    }

    setImgList([]);
    try {
      const containerWidth = boxRef?.clientWidth;
      const containerHeight = boxRef?.clientHeight;
      setSizes(newSizes);
      setIsError(false);

      const isPdf = imgListParam?.length > 0;
      const imageListToProcess = isPdf ? imgListParam : [{ ImageUrl: imgUrl }];
      setShowPdfPageControl(imgListParam?.length > 1);

      // 使用 imgSize 或从图片获取自然尺寸
      const naturalImgInfo =
        imgSize || (await getImgNatural(curImg, isPdf, shouldStripExif));
      setImgInfo(naturalImgInfo);
      setCurImgSize(naturalImgInfo); // 保存图片尺寸

      const result = getScaleRatio({
        ImageBox: { h: containerHeight, w: containerWidth },
        Image: {
          h: isImgHorizontal(deg)
            ? naturalImgInfo.width
            : naturalImgInfo.height,
          w: isImgHorizontal(deg)
            ? naturalImgInfo.height
            : naturalImgInfo.width,
        },
      });
      setRatio(result.Ratio);

      let processedImgList = imageListToProcess.map((img) => ({
        ...img,
        src: img.ImageUrl,
        imgRadio: naturalImgInfo.height
          ? naturalImgInfo.height / img.Height || 1
          : 1,
        width: result.Ratio * naturalImgInfo.width,
        height: result.Ratio * naturalImgInfo.height,
        stripExif: shouldStripExif,
      }));

      setContainerImgInfo({
        width: result.Ratio * naturalImgInfo.width,
        height: result.Ratio * naturalImgInfo.height,
      });

      processedImgList = processedImgList.map((item, index) => ({
        ...item,
        x: 0,
        y:
          getSum(processedImgList.slice(0, index).map((i) => i.height)) +
            index * PDF_GAP || 0,
      }));

      const newTargetHeight = getSum(processedImgList.map((i) => i.height));
      const newTargetWidth = getSum(processedImgList.map((i) => i.width));

      setTotalPage(processedImgList.length);
      setImgList(processedImgList);
      handleGetStageInfo({
        curDeg: deg,
        angle: 0, // ignore OCR angle
        targetHeight: newTargetHeight,
        ratio: result.Ratio,
        imgInfo: naturalImgInfo,
      });
      getImgRotationInfo({
        ratio: result.Ratio,
        imgInfo: naturalImgInfo,
        angle: 0, // ignore OCR angle
      });
      setTargetHeight(newTargetHeight);
      setTargetWidth(newTargetWidth);
    } catch (e) {
      console.error("Stage width error:", e);
      setIsError(true);
    }
  };

  /**
   * 处理滚动页面
   */
  const handleSetScrollPage = useCallback(() => {
    if (!imageBoxRef.current || imgListRef.current.length < 1) return;

    const scrollElement = imageBoxRef.current;
    const pageHeight =
      containerImgInfoRef.current.height * (sizesRef.current.width / 100) + 10;
    const scrollPage = Math.ceil(scrollElement.scrollTop / pageHeight) + 1;
    const finishPage = Math.min(scrollPage, imgListRef.current.length);

    setCurrentPage(finishPage || 1);
    setInputVal(finishPage || 1);
  }, []);

  /**
   * 查找当前矩形
   */
  const handleFindCurRect = async (selectRectKey, rectList) => {
    const imgUrl = imageList?.length > 0 ? imageList[0] : imageUrl;
    if (!imgUrl) return;

    const curRect = rectList.find((rect) => rect.key === selectRectKey);
    if (!curRect) return;

    const pageNum = curRect.pageNum || 1;
    setCurrentPage(pageNum);
    setInputVal(pageNum);
    scrollToTarget(pageNum);

    if (needClip && curRect.points) {
      const imageBase64 = await cropToBase64(
        imgUrl,
        [
          { x: curRect.points[0], y: curRect.points[1] },
          { x: curRect.points[2], y: curRect.points[3] },
          { x: curRect.points[4], y: curRect.points[5] },
          { x: curRect.points[6], y: curRect.points[7] },
        ],
        curImgSize,
      );

      if (imageBase64) {
        onClip({ img: imageBase64, curRect });
      }
    }
  };

  // ============ 缩放操作 - 参考原始代码 ============

  /**
   * 放大
   */
  // 保存当前 stripExif 状态，供缩放/还原时复用
  const [curStripExif, setCurStripExif] = useState(false);

  const onZoomIn = () => {
    const newWidth = sizes.width + ZOOM_STEP;
    if (newWidth <= ZOOM_MAX) {
      const newSizes = {
        ...sizes,
        width: newWidth,
        height: newWidth,
      };
      handleSetStageWidth({
        imageUrl,
        imageList,
        imgSize: curImgSize,
        sizes: newSizes,
        imageBoxRef: imageBoxRef?.current,
        curDeg,
        stripExif: curStripExif,
      });
      handleSetScrollPage();
    }
  };

  /**
   * 缩小
   */
  const onZoomOut = () => {
    const newWidth = sizes.width - ZOOM_STEP;
    if (newWidth > ZOOM_MIN) {
      const newSizes = {
        ...sizes,
        width: newWidth,
        height: newWidth,
      };
      handleSetStageWidth({
        imageUrl,
        imageList,
        imgSize: curImgSize,
        sizes: newSizes,
        imageBoxRef: imageBoxRef?.current,
        curDeg,
        stripExif: curStripExif,
      });
      handleSetScrollPage();
    }
  };

  /**
   * 快速还原
   */
  const onZoom = () => {
    handleSetStageWidth({
      imageUrl,
      imageList,
      imgSize: curImgSize,
      sizes: {
        ...sizes,
        width: 100,
        height: 100,
        deg: 0,
      },
      imageBoxRef: imageBoxRef?.current,
      curDeg,
      stripExif: curStripExif,
    });
    handleSetScrollPage();
  };

  /**
   * 旋转
   */
  const onDeg = (deg) => {
    const cur = curDeg + deg * 90;
    setCurDeg(cur);
  };

  // ============ 页面导航 - 参考原始代码 ============

  /**
   * 验证并跳转页面
   */
  const validateAndGoToPage = (value) => {
    if (!REGEX.NUMBER.test(value)) {
      setInputVal(currentPage);
      return;
    }

    if (value > totalPage) {
      setCurrentPage(totalPage);
      scrollToPage(totalPage);
    } else if (value <= 1) {
      setCurrentPage(1);
      scrollToPage(1);
    } else {
      setCurrentPage(Number(value));
      scrollToPage(Number(value));
    }
  };

  // ============ Imperative Handle ============

  useImperativeHandle(cRef, () => ({
    onPreChange: ({ curImg, curpPdfList, imgSize, stripExif = false }) => {
      setLoading(true);
      setCurDeg(0);
      setCurImgSize(imgSize);
      setCurStripExif(stripExif);
      handleSetStageWidth({
        imageUrl: curImg,
        imageList: curpPdfList,
        imgSize,
        sizes: { ...INITIAL_SIZES },
        imageBoxRef: imageBoxRef?.current,
        curDeg: 0,
        stripExif,
      });
      setLoading(false);
    },
    onRectChange: async () => {},
    // 多页预览：滚动定位到指定页
    onScrollToPage: (page) => {
      const total = imgListRef.current?.length || 0;
      if (!total) return;
      const target = Math.min(Math.max(Number(page) || 1, 1), total);
      setCurrentPage(target);
      setInputVal(target);
      scrollToTarget(target);
    },
  }));

  // ============ Effects ============

  // 同步 ref 值
  useEffect(() => {
    containerImgInfoRef.current = containerImgInfo;
  }, [containerImgInfo]);
  useEffect(() => {
    imgListRef.current = imgList;
  }, [imgList]);
  useEffect(() => {
    sizesRef.current = sizes;
  }, [sizes]);

  // 选中矩形变化
  useEffect(() => {
    if (selectRect && rects.length > 0) {
      handleFindCurRect(selectRect, rects);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectRect]);

  // eduAgent 自动选择第一项
  useEffect(() => {
    if (rects.length > 0 && needClip) {
      onSelect(rects[0].key);
    }
  }, [rects, needClip, onSelect]);

  // 旋转角度变化 - 只在 curDeg 变化时触发，使用最新的状态值
  useEffect(() => {
    // 确保有基础数据才进行旋转计算
    if (ratio > 0 && targetHeight > 0) {
      handleGetStageInfo({
        curDeg,
        angle: 0,
        imgInfo,
        targetWidth,
        targetHeight,
        ratio,
      }); // ignore OCR angle
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curDeg]);

  // 滚动监听
  useEffect(() => {
    const debouncedScroll = debounce(
      handleSetScrollPage,
      SCROLL_DEBOUNCE_DELAY,
    );
    const scrollElement = imageBoxRef.current;

    if (scrollElement) {
      scrollElement.addEventListener("scroll", debouncedScroll, {
        passive: true,
      });
      handleSetScrollPage();
    }

    return () => {
      if (scrollElement) {
        scrollElement.removeEventListener("scroll", debouncedScroll);
      }
    };
  }, [handleSetScrollPage]);

  // ============ 计算缩放比例 ============
  const scaleRatio = sizes.width / 100;

  /**
   * 计算内容包装器的样式
   * 用于撑开滚动区域并居中 Stage
   *
   * 关键设计：
   * - wrapper 使用 inline-flex 让其宽度由内容决定
   * - 外层容器已有 overflow: auto 支持滚动
   * - min-width/min-height: 100% 确保小图片时 wrapper 填满容器实现居中
   * - 当 Stage 超出容器时，wrapper 会被撑大，从而触发滚动条
   */
  const stageWidth = stageInfo.width * scaleRatio;
  const stageHeight = stageInfo.height * scaleRatio;

  const wrapperStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "100%",
    minHeight: "100%",
    // wrapper 的实际尺寸由内部 Stage 撑开
    width: stageWidth > 0 ? "max-content" : "100%",
    height: stageHeight > 0 ? "max-content" : "100%",
  };

  // ============ 渲染矩形标签 ============
  const renderRectLabel = (rect, isSelected, pageYOffset, pageRadio) => {
    if (!(needClip || showLabel || rect?.showTitleLabel) || !rect?.points || !rect.label) {
      return null;
    }

    // 切题（edu）：题号固定白字 + 彩色底，贴在框左上角
    const isTitleLabel = !!rect.showTitleLabel;
    const boxTop = pageYOffset + rect.points[1] * scaleRatio * ratio * pageRadio;
    const boxLeft = rect.points[0] * scaleRatio * ratio * pageRadio;

    return (
      <Html
        className="htmlPoint"
        parentNodeFunc={(args) => args.stage?.container()}
        divProps={{
          style: {
            position: "absolute",
            width: isTitleLabel ? "auto" : "60px",
            textAlign: "center",
            borderRadius: "3px",
            ...(isTitleLabel
              ? getHtmlPositionByDeg(boxTop - 16, boxLeft, curDeg)
              : getHtmlPositionByDeg(boxTop, boxLeft - 52, curDeg)),
            color: isTitleLabel
              ? "#fff"
              : isSelected
                ? "#fff"
                : rect.titleStyle?.activeColor || "#4A70FF",
            background: isTitleLabel
              ? rect.labelBg || "#4A70FF"
              : isSelected
                ? rect.titleStyle?.activeColor || "#4A70FF"
                : rect.titleStyle?.bgColor || "#AACFFF",
          },
        }}
      >
        <div className="edu-title">{rect.label}</div>
      </Html>
    );
  };

  // ============ 渲染正确/错误图标 ============
  const renderCorrectIcon = (rect, pageYOffset, pageRadio) => {
    if (!showLabel || !rect?.points || rect.IsCorrect === undefined)
      return null;

    return (
      <Html
        className="htmlPoint isCorrectIcon"
        parentNodeFunc={(args) => args.stage?.container()}
        divProps={{
          style: {
            position: "absolute",
            width: "16px",
            height: "16px",
            textAlign: "center",
            ...getHtmlPositionByDeg(
              pageYOffset +
                rect.points[5] * scaleRatio * ratio * pageRadio -
                16,
              rect.points[4] * scaleRatio * ratio * pageRadio - 16,
              curDeg,
            ),
          },
        }}
      >
        <div
          className={`edu-correct-icon small ${rect.IsCorrect ? "correct" : "error"}`}
        ></div>
      </Html>
    );
  };

  // ============ 渲染 ============

  if (currentImage?.cantPreview) {
    // 允许业务侧传入中性文案（如「已上传 PDF，点击抽取后按页展示」），
    // 有自定义文案时不再用报错图标，避免看起来像失败
    const tipMsg = currentImage?.cantPreviewMsg;
    return (
      <div
        className={`unv-thumbnail-preview center ${tipMsg ? "isInfo" : ""} ${isShow ? "active" : "hidden"}`}
        style={style}
        ref={imageBoxRef}
      >
        <div className="thumbnail-errorcontent">
          {!tipMsg && (
            <div className="thumbnail-errorcontent__img">
              <ErrorIcon />
            </div>
          )}
          <div className="thumbnail-errorcontent__msg">
            {tipMsg || "PDF 文件暂不支持预览"}
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <>
        <div
          className={`unv-thumbnail-preview center ${isShow ? "active" : "hidden"}`}
          style={style}
          ref={imageBoxRef}
        >
          <div className="thumbnail-errorcontent">
            <div className="thumbnail-errorcontent__img">
              <ErrorIcon />
            </div>
            <div className="thumbnail-errorcontent__msg">{errorMsg}</div>
            <Button type="link" onClick={onUpload}>
              重新上传
            </Button>
          </div>
        </div>
        {(isLoading || loading) && <LoadingOverlay />}
      </>
    );
  }

  return (
    <>
      <div
        className={`unv-thumbnail-preview ocr-thumbnail-preview ${isShow ? "active" : "hidden"}`}
        style={style}
        ref={imageBoxRef}
        id="imageBoxRef"
      >
        {/* 内部包装器：撑起滚动区域并居中 Stage */}
        <div style={wrapperStyle}>
          <Stage
            ref={layerRef}
            height={stageInfo.height * scaleRatio}
            width={stageInfo.width * scaleRatio}
            style={{
              flexShrink: 0,
              width: stageInfo.width * scaleRatio,
              height: stageInfo.height * scaleRatio,
            }}
          >
            <Layer
              rotation={rotateInfo.rotation}
              offsetX={rotateInfo.offsetX * scaleRatio}
              offsetY={rotateInfo.offsetY * scaleRatio}
            >
              {imgList?.map((item, index) => (
                <LayerImage
                  key={index}
                  width={item.width * scaleRatio}
                  height={item.height * scaleRatio}
                  rotation={imgRotateInfo.rotation}
                  offsetX={imgRotateInfo.offsetX}
                  offsetY={imgRotateInfo.offsetY}
                  src={item.src}
                  y={item.y * scaleRatio}
                  x={item.x * scaleRatio}
                  stripExif={item.stripExif || false}
                />
              ))}
              {!loading &&
                rects.length > 0 &&
                rects.map((rect, i) => {
                  const pageNum = rect.pageNum || 1;
                  const pageRadio = imgList[pageNum - 1]?.imgRadio || 1;
                  const pageYOffset =
                    (imgList[pageNum - 1]?.y || 0) * scaleRatio;
                  const selectedRectData = selectRect
                    ? rects.find((r) => r.key === selectRect)
                    : null;
                  const isSelected =
                    selectRect &&
                    rect.key &&
                    (selectRect === rect.key ||
                      // 跨栏/跨页题有多个坐标框，它们属于同一道题，需一起高亮
                      (selectedRectData?.groupKey &&
                        rect.groupKey === selectedRectData.groupKey) ||
                      (selectedRectData?.points &&
                        rect.points &&
                        JSON.stringify(selectedRectData.points) ===
                          JSON.stringify(rect.points)));

                  return (
                    <React.Fragment key={i}>
                      {renderRectLabel(
                        rect,
                        isSelected,
                        pageYOffset,
                        pageRadio,
                      )}
                      {renderCorrectIcon(rect, pageYOffset, pageRadio)}
                      {rect.points && (
                        <Line
                          onClick={() => {
                            if (!rect.cantSelect) {
                              onSelect?.(rect.key);
                            }
                          }}
                          points={rect.points.map((val, idx) =>
                            idx % 2 === 0
                              ? val * scaleRatio * ratio * pageRadio
                              : pageYOffset +
                                val * scaleRatio * ratio * pageRadio,
                          )}
                          dash={rect.dash}
                          fill={
                            isSelected
                              ? rect.activeFill || "rgba(0, 110, 255, 0.12)"
                              : rect.fill || "transparent"
                          }
                          stroke={
                            isSelected
                              ? rect.activeStoke || "#4A70FF"
                              : rect.stroke || "#AACFFF"
                          }
                          strokeWidth={
                            isSelected
                              ? rect.activeStrokeWidth || 2
                              : rect.stokeWidth || 1
                          }
                          closed
                        />
                      )}
                    </React.Fragment>
                  );
                })}
            </Layer>
          </Stage>
        </div>
      </div>

      {/* 工具栏 */}
      <div
        className="unv-thumbnail-preview__toolbar"
        style={{
          right: `calc(50% - ${showPdfPageControl ? "121px" : "48px"})`,
        }}
      >
        {showPdfPageControl && (
          <div className="unv-thumbnail-preview__toolbar__pdfpreview">
            <Icon
              className={`pre ${currentPage <= 1 ? "disable" : ""}`}
              name="direction-bottom"
              onClick={() => {
                if (currentPage <= 1) {
                  setCurrentPage(1);
                  scrollToPage(1);
                } else {
                  setCurrentPage(Number(currentPage) - 1);
                  scrollToPage(Number(currentPage) - 1);
                }
              }}
            />
            <span className="currentPage">
              <Input
                value={inputVal}
                style={{ width: "32px" }}
                onKeyUp={(e) =>
                  e.keyCode === 13 && validateAndGoToPage(inputVal)
                }
                onBlur={() => validateAndGoToPage(inputVal)}
                onChange={(val) => setInputVal(val)}
              />
            </span>
            <span className="split">/</span>
            <span className="totalPage">{totalPage}</span>
            <Icon
              className={`next ${currentPage >= totalPage ? "disable" : ""}`}
              name="direction-bottom"
              onClick={() => {
                if (currentPage >= totalPage) {
                  setCurrentPage(totalPage);
                  scrollToPage(totalPage);
                } else {
                  setCurrentPage(Number(currentPage) + 1);
                  scrollToPage(Number(currentPage) + 1);
                }
              }}
            />
          </div>
        )}
        {showZoomInIcon && (
          <Tooltip content="放大" theme="light">
            <div
              style={{ marginRight: "8px" }}
              className={`ocrDemo-icon ocrDemo-icon-zoomInIcon ${sizes.width >= ZOOM_MAX ? "disabled" : ""}`}
              onClick={onZoomIn}
            />
          </Tooltip>
        )}
        {showZoomOutIcon && (
          <Tooltip content="缩小" theme="light">
            <div
              style={{ marginRight: "16px" }}
              className={`ocrDemo-icon ocrDemo-icon-onZoomOut ${sizes.width <= ZOOM_MIN ? "disabled" : ""}`}
              onClick={onZoomOut}
            />
          </Tooltip>
        )}
        {showLeftIcon && (
          <Tooltip content="向左旋转90度" theme="light">
            <div
              style={{ marginRight: "8px" }}
              className="ocrDemo-icon ocrDemo-icon-leftIcon"
              onClick={() => onDeg(-1)}
            />
          </Tooltip>
        )}
        {showRightIcon && (
          <Tooltip content="向右旋转90度" theme="light">
            <div
              style={{ marginRight: "16px" }}
              className="ocrDemo-icon ocrDemo-icon-rightIcon"
              onClick={() => onDeg(1)}
            />
          </Tooltip>
        )}
        {showZoomIcon && (
          <Tooltip content="快速还原" theme="light">
            <div
              className={`ocrDemo-icon ocrDemo-icon-onZoom ${sizes.width === 100 ? "disabled" : ""}`}
              onClick={onZoom}
            />
          </Tooltip>
        )}
      </div>

      {(isLoading || loading) && <LoadingOverlay />}
    </>
  );
};

export default ThumbnailPreview;
